import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AppLogo from "../components/AppLogo";
import { Wrapper } from "./styles/RegisterStyle";
import errorHandler from "../utils/errorHandler";
import { resendVerification, verifyEmail } from "../controller/email/emailController";
import { getCurrentUser } from "../controller/users/usersController";
import { fetchStartupContents } from "../graphQL/controller/startupContentsController";

const COOLDOWN_MS = 15000;

const EmailVerification = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { credObj } = useSelector((store) => store.auth);
  const { currentUser } = useSelector((store) => store.users);

  const [otp, setOtp] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const [cooldownUntil, setCooldownUntil] = useState(() => Date.now() + COOLDOWN_MS);
  const [now, setNow] = useState(Date.now());

  const navigatedRef = useRef(false);
  const autoResentRef = useRef(false);

  const { email, fromLogin } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const emailFromQuery = params.get("email") || "";
    const from = params.get("from") || "";
    return {
      email: emailFromQuery || location.state?.email || currentUser?.email || "",
      fromLogin: from === "login" || location.state?.fromLogin === true,
    };
  }, [location.search, location.state?.email, location.state?.fromLogin, currentUser?.email]);

  const remainingSeconds = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));
  const canResend = Boolean(email) && remainingSeconds === 0 && !isResending;

  const isOtpValid = /^\d{6}$/.test(otp);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 250);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!email) return;
    setCooldownUntil(Date.now() + COOLDOWN_MS);
  }, [email]);

  useEffect(() => {
    if (!fromLogin) return;
    if (!email) return;
    if (autoResentRef.current) return;

    // When user comes from Login, server didn't send OTP automatically.
    autoResentRef.current = true;
    (async () => {
      setIsResending(true);
      try {
        await dispatch(resendVerification({ email })).unwrap();
        toast.success("Verification code sent");
        setCooldownUntil(Date.now() + COOLDOWN_MS);
      } catch (err) {
        errorHandler(err);
      } finally {
        setIsResending(false);
      }
    })();
  }, [fromLogin, email, dispatch]);

  useEffect(() => {
    if (navigatedRef.current) return;
    if (credObj && currentUser?.isEmailVerified === true) {
      navigatedRef.current = true;
      navigate("/", { replace: true });
    }
  }, [credObj, currentUser?.isEmailVerified, navigate]);

  const handleOtpChange = (e) => {
    const digitsOnly = (e.target.value || "").replace(/\D/g, "").slice(0, 6);
    setOtp(digitsOnly);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!email) {
      errorHandler("Missing email. Please login/register again.");
      return;
    }
    if (!isOtpValid) return;

    setIsVerifying(true);
    try {
      await dispatch(verifyEmail({ email, otp })).unwrap();
      toast.success("Email verified");

      if (credObj) {
        dispatch(getCurrentUser());
        dispatch(fetchStartupContents());
      }

      navigate("/", { replace: true });
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      errorHandler("Missing email. Please login/register again.");
      return;
    }
    if (!canResend) return;

    setIsResending(true);
    try {
      await dispatch(resendVerification({ email })).unwrap();
      toast.success("Verification code resent");
      setCooldownUntil(Date.now() + COOLDOWN_MS);
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Wrapper className="full-page">
      <form className="form" onSubmit={handleVerify}>
        <AppLogo containerStyle="logo-container" textStyle="logo-text" />
        <h3>Email Verification</h3>

        <p style={{ marginBottom: "0.75rem" }}>
          Enter the 6-digit code sent to{email ? ` ${email}` : " your email"}.
        </p>

        <div className="form-row">
          <label className="form-label">OTP</label>
          <input
            className="form-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={otp}
            onChange={handleOtpChange}
            placeholder="6-digit code"
          />
        </div>

        <button
          className="btn btn-block"
          type="submit"
          disabled={!isOtpValid || isVerifying}
        >
          {isVerifying ? "Verifying..." : "Verify"}
        </button>

        <p style={{ marginTop: "0.75rem" }}>
          <button
            type="button"
            onClick={handleResend}
            className="member-btn"
            disabled={!canResend}
          >
            {remainingSeconds > 0
              ? `Resend in ${remainingSeconds}s`
              : isResending
                ? "Resending..."
                : "Resend"}
          </button>
        </p>

        {!email && (
          <p style={{ marginTop: "0.75rem" }}>
            <button
              type="button"
              className="member-btn"
              onClick={() => navigate("/register", { replace: true })}
            >
              Go to Login
            </button>
          </p>
        )}
      </form>
    </Wrapper>
  );
};

export default EmailVerification;
